import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-progress-bar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { config } from '@ils/config/config.js';
import { reportProblemForSlot } from '@ils/slots/api/slot.js';
import { UserRole } from '@ils/static/enums.js';
import { html, noChange, nothing } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import type { ILSUser } from '../../db/ils-user.js';
import { ExamStateEnum, type ExamState } from '../examTypes.js';

@customElement('ils-exam-topbar')
export class ILSExamTopbar extends ChemistryLitElement {
  @state() examState?: ExamState;
  @state() user = new RXController(this, currentUser<ILSUser>());

  // eslint-disable-next-line complexity
  override render() {
    if (!this.examState) return noChange;
    return html`
      <x-flex>
        <x-progress-bar
          .value="${this.examState.progress}"
          tone=${ifDefined(this.examState.progress > 0.9 ? 'error' : undefined)}
          .transitionDuration="${1}"
          ${css(this.examState.progress >= 0.95 ? { height: this.spacing.md } : { height: this.spacing.sm })}
        ></x-progress-bar>
        <x-grid
          columns="3"
          rows="min-content"
          padding="sm"
          ${css({
            borderBottom: `2px solid ${this.colors.neutral.shade(100)}`,
            zIndex: '1',
          })}
        >
          <x-text>
            ${this.user.value?.ecommerce?.fullName}${!this.examState.slot?.recordAudio
              ? nothing
              : `: Situation ${(this.examState.index ?? 0) + 1} von
            ${this.examState.slotCount}`}
          </x-text>
          ${this.durationDisplay()}
          ${this.examState.state === ExamStateEnum.PAUSE
            ? nothing
            : html`
                <x-flex
                  horizontal
                  end
                  crossaxis-center
                  space="sm"
                  @click=${async () => {
                    if (!this.examState?.slot) return;
                    const confirm = await XDialog.confirm(
                      !this.examState?.slot?.recordAudio
                        ? html`
                            <x-flex space="sm" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
                              <x-h3> Wollen Sie wirklich ein technisches Problem melden? </x-h3>
                              <x-text>
                                Ein Administrator wird benachrichtigt und sich zeitnah um das Problem kümmern und Sie
                                eventuell diesbezüglich kontaktieren. Bitte überprüfen Sie ihre Internet Verbindung
                                bevor Sie ein Problem melden. Sich Sie sicher, dass Sie ein technisches Problem melden
                                wollen?
                              </x-text>
                            </x-flex>
                          `
                        : html`
                            <x-flex space="sm" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
                              <x-h3> Sind Sie sicher? </x-h3>
                              <x-text>
                                Ein Administrator wird zu Ihnen kommen. Die Prüfung wird dann unterbrochen. Sie wird nur
                                wiederholt, wenn tatsächlich ein technisches Problem vorliegt.
                              </x-text>
                            </x-flex>
                          `,
                    );
                    if (confirm) return reportProblemForSlot(this.examState.slot._id)();
                  }}
                >
                  ${this.examState.userRole !== UserRole.TRAINEE
                    ? html`
                        <x-button
                          mode="text"
                          icon="call"
                          href="tel:${config.get('TECHNICALSUPPORTPHONE')}"
                          target="_blank"
                          >${config.get('TECHNICALSUPPORTPHONE')}</x-button
                        >
                      `
                    : nothing}
                  <x-button mode="text" tone="error" icon="healing"> technische Hilfe anfordern </x-button>
                </x-flex>
              `}
        </x-grid>
      </x-flex>
    `;
  }

  // eslint-disable-next-line complexity
  private durationDisplay() {
    if (!this.examState) return nothing;

    let bonusTime = 0;
    const topTimer = this.examState.topTimer;
    let originalTimer = topTimer;
    let isInBonus = true;
    let isExtended = false;

    if (this.examState.slot?.endTime) {
      const startTime = this.examState.slot.startTime;
      const endTime = this.examState.slot.endTime;
      const totalLength = endTime.diff(startTime);
      const osceDuration = this.examState.slot.osce?.durationInMinutes;
      isExtended = totalLength.as('minutes') > osceDuration;

      bonusTime =
        isExtended && this.examState.state !== ExamStateEnum.PAUSE ? totalLength.as('minutes') - osceDuration : 0;

      originalTimer = topTimer.minus(bonusTime * 60 * 1000);
      isInBonus = originalTimer.as('minutes') < 0;
    }

    if (!topTimer.as('seconds') && !isExtended) return html` <div></div> `;
    return html`
      <x-flex crossaxis-center>
        <x-flex horizontal crossaxis-center space="xs">
          <x-text>
            ${this.examState.state === ExamStateEnum.PAUSE
              ? 'Zeit bis zur nächsten Situation'
              : 'Zeit bis Ende der Situation'}
            ${!isInBonus ? originalTimer.toFormat('mm:ss') : '00:00'}
          </x-text>
          ${isExtended &&
          (this.examState.state === ExamStateEnum.RUNNING || this.examState.state === ExamStateEnum.STARTED_EARLY)
            ? html`
                <x-text ${css({ color: this.colors.tone.error })}>
                  (${!isInBonus ? '+' : ''}${isInBonus ? topTimer.toFormat('mm:ss') : bonusTime} min)
                </x-text>
              `
            : nothing}
        </x-flex>
      </x-flex>
    `;
  }
}
